import { Component } from 'preact';
import type { ComponentChildren } from 'preact';
import { AlertTriangle } from 'lucide-preact';
import { Button } from '../primitives/Button';
import { Icon } from '../primitives/Icon';

interface Props {
  children: ComponentChildren;
  fallback?: (error: Error, reset: () => void) => ComponentChildren;
  onError?: (error: Error) => void;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static override getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error) {
    this.props.onError?.(error);
  }

  reset = () => this.setState({ error: null });

  override render() {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback(this.state.error, this.reset);
      return (
        <div className="flex flex-col items-center justify-center gap-3 rounded-md border border-border-subtle bg-error-bg px-6 py-10 text-center">
          <Icon as={AlertTriangle} size={28} className="text-error" />
          <div>
            <p className="text-sm font-semibold text-fg-strong">Algo ha fallado</p>
            <p className="mt-1 text-xs text-fg-muted">{this.state.error.message}</p>
          </div>
          <Button size="sm" variant="primary" onClick={this.reset}>
            Reintentar
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}
